import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription, from, of, lastValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CardButtonComponent } from 'src/app/components/ui-components/buttons/card-button/card-button.component';
import { CardButtonBackground, CardButtonSize } from 'src/app/components/ui-components/buttons/card-button/card-button.model';
import { DropdownComponent } from 'src/app/components/ui-components/dropdown/dropdown.component';
import { DropdownOption } from 'src/app/components/ui-components/dropdown/dropdown.model';
import { User } from 'src/app/services/model/user.model';
import { SharedModule } from "../../../shared.module";
import { DbSchemeEnum } from 'src/app/enums/db-scheme.enum';
import { TenderType } from 'src/app/services/model/tender-type.enum';
import { PopUpInformalType } from 'src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model';
import { BaseClosingComponent } from '../base-closing.component';
import { ProcedureNameEnum } from 'src/app/enums/procedure-name.enum';
import { IExecuteProcedureResult, IGetPaymentTypesResultData } from 'src/app/services/model/pos.api.model';
import { HeaderProcessingService } from 'src/app/services/header-processing.service';
import { ReportExportService } from 'src/app/services/report-export.service';
import { ReportType } from 'src/app/services/model/reportType.model';
import { LoginService } from 'src/app/services/login.service';
import { SharedService } from 'src/app/services/shared.service';
import { PaymentTypesService } from 'src/app/services/payment-types.service';
import { VoucherExchangeComponent } from '../../financial-documents/voucher-exchange/voucher-exchange.component';

interface Denomination {
  value: number;
  count: number;
  registeredCount: number;
  total: number;
}

interface ClosingDenominationsParams {
  BusinessUnitID: number;
  PaymentName: string;
  ReturnToResult: number;
}

interface ClosingDenominationsResult {
  BusinessUnitID: number;
  TenderType: string;
  TotalCashMovement: number;
  TotalLocalCashMovement: number;
  RecordCount: number;
  Nominals: {
    NominalValue: number;
    NominalCount: number;
    NominalTotal: number;
  }[];
  CalculatedDateTime: string;
  LastClosingDateTime: string;
  CurrencyCode: string;
}

@Component({
  selector: 'app-valuables-closing',
  templateUrl: './valuables-closing.component.html',
  styleUrls: ['./valuables-closing.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, CardButtonComponent, DropdownComponent, SharedModule, VoucherExchangeComponent],
})
export class ValuablesClosingComponent extends BaseClosingComponent implements OnInit, OnDestroy {
  currentDate = new Date();
  closingForm: FormGroup;
  user: User;
  currencyCode: string;
  
  valuableTypes: DropdownOption[] = [];

  denominations: Denomination[] = [];
  allDenominationsByType: Map<string, Denomination[]> = new Map();
  paymentTypesData: IGetPaymentTypesResultData[] = [];

  totalValue = 0;
  totalRegisteredValue = 0;
  registeredSummaryValue = 0;
  difference = 0;
  shortage = 0;
  surplus = 0;

  showVoucherExchangeForm = false;
  voucherExchangeFromPaymentType: string | null = null;

  CardButtonSize = CardButtonSize;
  CardButtonBackground = CardButtonBackground;

  get selectedValuableTypeLabel(): string | null {
    const selectedType = this.closingForm.get('valuableType')?.value;
    return this.valuableTypes.find(type => type.value === selectedType)?.label ?? null;
  }

  // get shouldShowConfirmationNote(): boolean {
  //   const selectedType = this.closingForm.get('valuableType')?.value;
  //   if (!selectedType) {
  //     return false;
  //   }

  //   const paymentType = this.paymentTypesData.find(type => type.Name === selectedType);
  //   return paymentType?.TenderType === TenderType.Cheque;
  // }

  private valuableTypeChangeSubscription: Subscription;
  private headerProcessingService = inject(HeaderProcessingService);
  private reportExportService = inject(ReportExportService);
  private _loginService = inject(LoginService);

  constructor(
  ) {
    super();
    // Initialize form with empty controls to prevent template errors
    this.closingForm = this.fb.group({
      accountingState: 0,
      confirm: false,
      valuableType: null,
      summaryValue: 0
    });
  }

  async ngOnInit() {
    const paymentTypes = await this._sharedService.getPaymentTypes();
    this.paymentTypesData = paymentTypes;
    
    // Filter payment types to only show vouchers and cheques
    const filteredPaymentTypes = paymentTypes.filter(pt => pt.ManualEntry);
    
    this.valuableTypes = filteredPaymentTypes
      
      .map(pt => ({ value: pt.Name, label: pt.Description }));

    // Subscribe to valuable type changes
    this.valuableTypeChangeSubscription = this.closingForm.get('valuableType').valueChanges.subscribe((selectedType) => {
      this.onValuableTypeChange(selectedType);
    });

    // Subscribe to form changes
    this.formChangesSubscription = this.closingForm.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
  }

  private async onValuableTypeChange(selectedType: string) {

    // Remove old denomination controls
    const currentDenominationControls = Object.keys(this.closingForm.controls)
      .filter(key => key.startsWith('denomination_'));
/**
    * Remove a FormControl from the component's closingForm by key without triggering value change events.
    * @example
    * removeControlByKey('denomination_10')
    * undefined
    * @param {string} key - The name of the control to remove (e.g. 'denomination_5').
    * @returns {void} Does not return a value.
    * @description
    *   - Calls this.closingForm.removeControl(key, { emitEvent: false }) to avoid emitting form valueChanges.
    *   - Safe to use while iterating over form controls (prevents spurious change handling).
    *   - Intended for removing dynamically created denomination_* controls when switching valuable type.
    */
    currentDenominationControls.forEach(key => {
      this.closingForm.removeControl(key, { emitEvent: false });
    });

    // Get denominations for selected type
    // Load denominations from cache if available
    
    this.spinner.show();

    const request = {
      Parameters: {
        BusinessUnitID: Number(this.user.SelectedBusinessUnit),
        PaymentName: selectedType,
        ReturnToResult: 0,
      },
      ProcedureName: ProcedureNameEnum.GetClosingDenominations,
      DbScheme: DbSchemeEnum.Dert,
    };

    const response = await this.sharedApiService.executeProcedure<ClosingDenominationsParams, IExecuteProcedureResult<ClosingDenominationsResult>>(request);

    this.totalRegisteredValue = response.data.TotalCashMovement || 0; /* Contains correct SUM, nominals SUM can be inaccurate because of DEPOSIT/WITHDRAWAL */
    
    const paymentTypes = await this.paymentTypesService.getPaymentTypesWithNominals();
    const paymentType = paymentTypes.find(pt => pt.Name === selectedType);

    if (!paymentType) {
      console.error(`Payment type not found: ${selectedType}`);
      this.denominations = [];
      this.spinner.hide();
      // show dialog error to user
      await this.popUpDialogService.confirm({
        title: 'webPos.Errors.Error',
        message: 'webPos.Closings.CheckPaymentConfiguration',
        subMessage: selectedType,
        type: PopUpInformalType.Error,
      });
      return;
    }

    let denominations = paymentType.Nominals?.map(n => {
      return {
        value: n.value,
        count: 0,
        registeredCount: 0,
        total: 0} as Denomination;
    }) || [];

    // Assign registered counts and totals from API response.
    // Only match against config-defined denominations; do NOT add API nominals
    // when TenderDenominationListConfig has no records for this payment type.
    if (denominations.length > 0) {
      response.data.Nominals?.forEach(nominalFromApi => {
        const match = denominations.find(d => d.value === nominalFromApi.NominalValue);
        if (match) {
          match.value = nominalFromApi.NominalValue;
          match.registeredCount = nominalFromApi.NominalCount;
          match.total = nominalFromApi.NominalTotal;
        }
      });
    } else {
      // No denominations configured – store the API total for the registered summary row
      this.registeredSummaryValue = response.data.TotalCashMovement || 0;
    }

    this.denominations = denominations;

    if (denominations.length > 0) {
      // Create form controls for denominations
      denominations.forEach((d, i) => {
        this.closingForm.addControl(`denomination_${d.value}`, this.fb.control(0), { emitEvent: false });
      });
      // Reset summary value
      this.closingForm.patchValue({ summaryValue: 0 }, { emitEvent: false });

      this.denominations.sort((a, b) => b.value - a.value);
    }

    this.calculateTotals();

    this.spinner.hide();

    this.closingForm.markAsDirty();
  }

  ngOnDestroy() {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
    if (this.valuableTypeChangeSubscription) {
      this.valuableTypeChangeSubscription.unsubscribe();
    }
  }

  calculateTotals() {
    if (!this.closingForm) {
      return;
    }

    let total = 0;
    let registeredTotal = 0;

    if (this.denominations.length > 0) {
      // Calculate from denominations
      this.denominations.forEach((d, i) => {
        const control = this.closingForm.get(`denomination_${d.value}`);
        const count = control ? control.value || 0 : 0;
        d.count = count;
        d.total = d.value * count;
        total += d.total || 0;
        registeredTotal += ((d.value || 0) * (d.registeredCount || 0));
      });
    } else {
      // Use summary value input
      total = this.closingForm.get('summaryValue')?.value || 0;
      registeredTotal = this.registeredSummaryValue;
    }
    
    this.totalValue = total;
    //this.totalRegisteredValue = registeredTotal;
    this.difference = this.totalValue - this.totalRegisteredValue;

    if (this.difference < 0) {
      this.shortage = Math.abs(this.difference);
      this.surplus = 0;
    } else {
      this.shortage = 0;
      this.surplus = this.difference;
    }
  }

  /**
   * Get the report type based on the selected payment type's TenderSubType and TenderType
   */
  private getReportTypeForPayment(valuableTypeName: string): ReportType | null {
    const paymentType = this.paymentTypesData.find(pt => pt.Name === valuableTypeName);
    
    if (!paymentType || paymentType.TenderType !== TenderType.Cheque) {
      return null;
    }

    switch (paymentType.TenderSubType?.toUpperCase()) {
      case 'EDENRED':
        return ReportType.edenred;
      case 'PLUXEE':
        return ReportType.pluxee;
      case 'UPCZ':
        return ReportType.upcz;
      default:
        return null;
    }
  }

  showVoucherExchange(currentPaymentTypeName?: string | null) {
    this.voucherExchangeFromPaymentType = currentPaymentTypeName ?? null;
    this.showVoucherExchangeForm = true;
  }

  hideVoucherExchange() {
    this.showVoucherExchangeForm = false;
    this.voucherExchangeFromPaymentType = null;
    // Reload the form data after voucher exchange
    this.reloadFormData();
  }

  private async reloadFormData() {
    const selectedType = this.closingForm.get('valuableType')?.value;
    if (selectedType) {
      await this.onValuableTypeChange(selectedType);
    }
  }

  async confirm(isMoneyClosing: boolean) {
    const res = await super.confirm(isMoneyClosing);

    if (res && res.success && !isMoneyClosing) {
      const valuableType = this.closingForm.get('valuableType')?.value;
      const reportType = valuableType ? this.getReportTypeForPayment(valuableType) : null;
      
      // Show success dialog with print option only if report type is available
      const dialogResult = await this.popUpDialogService.confirm({
        title: 'webPos.Done',
        message: 'webPos.Closings.InventoryCompleted',
        type: PopUpInformalType.Success,
        showPrintReceipt: true,
        printReceipt: true,
        showSendReceiptByEmail: true,
        sendReceiptByEmail: false,
        emailAddress: this._loginService.user?.Email, // prefill current user's email if available
      });

      if (res.trxUID && reportType) {
        const headerProcessingResult = await lastValueFrom(
          from(this.headerProcessingService.monitorHeader(res.trxUID)).pipe(
            catchError(error => { console.error('Header processing failed', error); return of(null); })
          )
        );

        const headerIdForPrint = headerProcessingResult
          ? this.headerProcessingService.getHeaderIdFromResult(headerProcessingResult)
          : null;

        if (headerIdForPrint) {
          console.log(`Valuables closing report is ready to print.`);

          if (dialogResult?.printReceipt) {
            await lastValueFrom(
              from(this.reportExportService.downloadPdf(
                undefined,
                [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
                true
              )).pipe(
                catchError(error => { console.error('PDF download failed', error); return of(null); })
              )
            );
          }

          if (dialogResult?.email) {
            await lastValueFrom(
              from(this.reportExportService.sendPdfByEmail(dialogResult.email, headerIdForPrint)).pipe(
                catchError(error => { console.error('Email sending failed', error); return of(null); })
              )
            );
          }
        }
      }

      this.router.navigate(['/dashboard']);
    }
    
    return res;
  }
}